/**
 * autoRecommendedRefs — Documents_On_Site Prompt 4.
 *
 * Pure default-selection rule that produces the initial documentRefs[]
 * for a report-generation wizard, given a site's current document
 * collection and the report type the user picked. Matches the spec:
 *
 *   relevance[reportType] >= 0.6              → userIncluded: true
 *   relevance[reportType] in [0.3, 0.6)       → userIncluded: false  (visible but unchecked)
 *   relevance[reportType] <  0.3              → omitted entirely     (still visible under "Other")
 *
 * Library-index docs are excluded — they're not user content. Docs
 * without any classifier output are treated as 0 (omitted) so first-run
 * wizards on legacy uploads don't silently pull in everything.
 */
import type { SiteDocument, SiteRagReport } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { ReportTypeId } from '../config/reportTypes';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';

export const AUTO_INCLUDE_THRESHOLD = 0.6;
export const VISIBLE_FLOOR_THRESHOLD = 0.3;

export function autoRecommendedRefs(
  site: SiteEntity | { documents?: SiteDocument[] } | undefined,
  reportType: ReportTypeId,
): NonNullable<SiteRagReport['documentRefs']> {
  const docs = (site?.documents ?? []).filter(d => !isLibraryIndexDoc(d));
  const refs: NonNullable<SiteRagReport['documentRefs']> = [];
  for (const d of docs) {
    const score = d.relevance?.[reportType];
    if (typeof score !== 'number' || !Number.isFinite(score)) continue;
    if (score >= AUTO_INCLUDE_THRESHOLD) {
      refs.push({ docId: d.id, relevance: score, userIncluded: true });
    } else if (score >= VISIBLE_FLOOR_THRESHOLD) {
      refs.push({ docId: d.id, relevance: score, userIncluded: false });
    }
    // < 0.3 → omitted entirely (the panel still surfaces them under "Other")
  }
  // Sort highest-first so the prompt's top-of-bullet ordering matches the
  // panel ordering. Stable: equal scores keep upload order.
  refs.sort((a, b) => b.relevance - a.relevance);
  return refs;
}

/**
 * RB3 — reference selection classification.
 *
 * Reports previously surfaced any `userIncluded: false` ref as "Excluded
 * by user", conflating two genuinely different states:
 *
 *   (a) The AI never auto-recommended the doc because its relevance fell
 *       below the auto-include floor ({@link AUTO_INCLUDE_THRESHOLD}).
 *       The user did NOTHING — it simply scored too low to be checked by
 *       default. This is a neutral, non-alarming state.
 *   (b) The user opened the DocumentSelectionPanel and explicitly turned
 *       the doc OFF. Only this case carries a user signal, which the
 *       panel records as `reasonIfExcluded`.
 *
 * The discriminator is the PRESENCE of `reasonIfExcluded`: the panel
 * stamps it whenever a user explicitly excludes a ref, and
 * autoRecommendedRefs never sets it (below-floor refs are emitted with
 * `userIncluded: false` and NO reason). So:
 *
 *   userIncluded === true                       → 'user-included'
 *   userIncluded === false + reasonIfExcluded    → 'user-excluded'
 *   userIncluded === false + no reasonIfExcluded → 'not-auto-selected'
 *
 * The `relevance` / threshold check is a belt-and-braces guard: a ref
 * whose score is at/above the auto-include floor yet still arrives
 * `userIncluded: false` WITHOUT a reason can only have been turned off
 * by a user (the auto rule would have checked it), so it classifies as
 * 'user-excluded' even if the reason string was lost in transit.
 */
export type RefSelectionState =
  | 'user-included'
  | 'user-excluded'
  | 'not-auto-selected';

/** Minimal shape needed to classify a ref — a `documentRefs[number]` fits. */
export interface ClassifiableRef {
  userIncluded: boolean;
  reasonIfExcluded?: string;
  relevance?: number;
}

/**
 * Classify a single documentRef into one of three selection states so the
 * references / appendix / DOCX surfaces can stop labelling below-floor
 * docs as "Excluded by user". Exported for those surfaces to adopt
 * (reportDocxExport included — owned by another agent, hence export-only).
 */
export function classifyRefSelection(ref: ClassifiableRef): RefSelectionState {
  if (ref.userIncluded) return 'user-included';
  // userIncluded === false from here on.
  const hasUserSignal =
    typeof ref.reasonIfExcluded === 'string' &&
    ref.reasonIfExcluded.trim().length > 0;
  if (hasUserSignal) return 'user-excluded';
  // No explicit reason. If the score was at/above the auto-include floor,
  // the auto rule WOULD have checked it — being off can only be a user
  // action (reason lost). Otherwise it was simply never auto-selected.
  const score = ref.relevance;
  if (
    typeof score === 'number' &&
    Number.isFinite(score) &&
    score >= AUTO_INCLUDE_THRESHOLD
  ) {
    return 'user-excluded';
  }
  return 'not-auto-selected';
}

/**
 * True only when the user gave an explicit "no" — distinct from a ref the
 * AI simply never auto-recommended. Convenience wrapper for surfaces that
 * just need the user-excluded / not-user-excluded distinction.
 */
export function isUserExcludedRef(ref: ClassifiableRef): boolean {
  return classifyRefSelection(ref) === 'user-excluded';
}

/**
 * True when a ref is below the auto-include floor and was never explicitly
 * touched by the user — the neutral "Not auto-selected (below relevance
 * floor)" state that must NOT be labelled "Excluded by user".
 */
export function isNotAutoSelectedRef(ref: ClassifiableRef): boolean {
  return classifyRefSelection(ref) === 'not-auto-selected';
}

/**
 * Human-readable label for each selection state. Centralised so the
 * references list, appendix, and DOCX export share one vocabulary.
 */
export function refSelectionLabel(state: RefSelectionState): string {
  switch (state) {
    case 'user-included':
      return 'Included';
    case 'user-excluded':
      return 'Excluded by user';
    case 'not-auto-selected':
      return 'Not auto-selected (below relevance floor)';
  }
}

/**
 * Determine the documentSelectionMode after a user edit. Pure helper so
 * the panel doesn't have to encode the rule inline.
 *
 *   - 'auto' → never edited (initial state from autoRecommendedRefs)
 *   - 'mixed' → user toggled an AI-recommended doc (above-threshold doc
 *     un-checked, or a [0.3, 0.6) doc checked)
 *   - 'manual' → user explicitly added a below-threshold doc OR removed
 *     the entire AI selection
 */
export function deriveSelectionMode(
  currentRefs: NonNullable<SiteRagReport['documentRefs']>,
  autoRefs: NonNullable<SiteRagReport['documentRefs']>,
): 'auto' | 'mixed' | 'manual' {
  const currentById = new Map(currentRefs.map(r => [r.docId, r]));
  const autoById = new Map(autoRefs.map(r => [r.docId, r]));

  // No edits at all? Sets must match exactly (id + userIncluded).
  if (currentRefs.length === autoRefs.length) {
    let identical = true;
    for (const a of autoRefs) {
      const c = currentById.get(a.docId);
      if (!c || c.userIncluded !== a.userIncluded) { identical = false; break; }
    }
    if (identical) return 'auto';
  }

  // Was anything pulled in that the AI didn't recommend at all?
  for (const c of currentRefs) {
    if (!autoById.has(c.docId) && c.userIncluded) {
      // The AI didn't suggest this doc OR scored it below the visible
      // floor; the user reached past the recommendation either way.
      return 'manual';
    }
  }
  return 'mixed';
}

/**
 * Workflow Prompt 8 — predicate for the "Classifying…" chip.
 *
 * A doc is pending classification (in-flight or yet-to-fire) when:
 *   - relevanceUpdatedAt is missing entirely (classifier never ran),
 *     OR
 *   - the relevance map is missing entirely, OR
 *   - the map exists but doesn't carry an entry for `reportType`
 *     (the user picked a type the classifier hasn't scored yet —
 *     which can happen when the classifier batched only the active
 *     report type at the time and a fresh wizard is now asking for
 *     a different one).
 *
 * The third branch matters because a doc can have
 * `relevance: { feasibility: 0.8 }` and STILL be pending for an
 * environmental wizard. Falling back to the "unscored" label there
 * would mislead the user — the classifier WILL score it; they just
 * haven't waited long enough.
 */
export function isPendingClassification(
  doc: SiteDocument,
  reportType: ReportTypeId,
): boolean {
  if (!doc.relevanceUpdatedAt) return true;
  if (!doc.relevance) return true;
  return !(reportType in doc.relevance);
}
